import {
  forwardRef,
  useRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { motion, useReducedMotion } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { springs } from "@samklang/design-tokens";
import { cn } from "../lib/cn";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "rounded-full text-base font-semibold tracking-snug",
    "min-h-12 px-6",
    "transition-[background-color,box-shadow,transform,opacity] duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    "select-none",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-foreground shadow-button hover:shadow-button-lg",
        secondary:
          "bg-secondary text-secondary-foreground shadow-button-secondary",
        soft: "bg-primary-soft text-primary shadow-none",
        ghost: "bg-transparent text-foreground-muted hover:bg-surface-muted/80 shadow-none",
        outline:
          "border border-border/80 bg-surface-raised/90 text-foreground shadow-none",
        danger: "bg-danger text-white shadow-button-danger",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      fullWidth: false,
    },
  }
);

export type ButtonProps = {
  children: ReactNode;
  className?: string;
  variant?: VariantProps<typeof buttonVariants>["variant"];
  fullWidth?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  id?: string;
  form?: string;
  name?: string;
  "aria-label"?: string;
  "aria-pressed"?: boolean;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant = "primary",
      fullWidth = false,
      loading = false,
      loadingLabel = "Laddar",
      disabled = false,
      children,
      onClick,
      type = "button",
      id,
      form,
      name,
      "aria-label": ariaLabel,
      "aria-pressed": ariaPressed,
    },
    ref
  ) {
    const reducedMotion = useReducedMotion();
    const lockRef = useRef(false);
    const isDisabled = disabled || loading;

    const handleClick: ButtonHTMLAttributes<HTMLButtonElement>["onClick"] = (
      event
    ) => {
      if (isDisabled || lockRef.current) {
        event.preventDefault();
        return;
      }
      lockRef.current = true;
      try {
        onClick?.(event);
      } finally {
        window.setTimeout(() => {
          lockRef.current = false;
        }, 400);
      }
    };

    const allowPressMotion = !reducedMotion && !isDisabled;

    return (
      <motion.button
        ref={ref}
        type={type}
        id={id}
        form={form}
        name={name}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        className={cn(buttonVariants({ variant, fullWidth }), className)}
        disabled={isDisabled}
        aria-busy={loading ? true : undefined}
        {...(allowPressMotion ? { whileTap: { scale: 0.975 } } : {})}
        transition={springs.responsive}
        onClick={handleClick}
      >
        {loading ? (
          <>
            <Spinner size="sm" label={loadingLabel} />
            <span className="opacity-90">{children}</span>
          </>
        ) : (
          children
        )}
      </motion.button>
    );
  }
);

export { buttonVariants };
